// Currency presentation for the broker portal — the SM-443 contract consumed, not
// forked (SM-396 phase 06b).
//
// The contract itself lives in `src/currency`, ported byte-for-byte from
// web-client (only the import specifiers differ, so this console's
// node-based test runner can execute the shared parity fixture). This
// module is the portal's thin adapter over it: one seed table for the whole app,
// plus the amount-entry unit the operator forms use.
//
// VALUE stays in src/lib/money.ts (int64 minor units, truncate never
// round). Every currency's mark is operator configuration now, so
// pre-multi-currency figure in this console is unchanged.

// Relative, extensioned imports: this console's test runner is
// `node --experimental-strip-types`, which has no bundler alias
// table, and a rule that cannot be executed by a test is a rule
// nobody is protecting.
import { SPARK_UNIT_MINOR, parseSparkToMinor } from "./money.ts";
import {
  CURRENCY_CODES as CONTRACT_CODES,
  isCurrencyCode as contractIsCurrencyCode,
  monogramGlyph as contractMonogramGlyph,
  resolveCurrencyPresentation,
  type CurrencyCode as ContractCurrencyCode,
} from "../currency/index.ts";

export const CURRENCY_CODES = CONTRACT_CODES;
export type CurrencyCode = ContractCurrencyCode;

export interface CurrencySeed {
  displayName: string;
  symbol: string;
  color: string;
}

/**
 * Frozen seed, DERIVED from the contract rather than repeated. That is
 * what keeps MM Web's unconfigured RED identical to Backoffice's and
 * Web Client's: there is no second table to forget to update.
 */
export const CURRENCY_SEED: Readonly<Record<CurrencyCode, CurrencySeed>> = Object.freeze(
  Object.fromEntries(
    CURRENCY_CODES.map((code) => {
      const p = resolveCurrencyPresentation(code);
      return [code, Object.freeze({ displayName: p.displayName, symbol: p.symbol, color: p.color })];
    }),
  ) as Record<CurrencyCode, CurrencySeed>,
);

export const isCurrencyCode = contractIsCurrencyCode;
export const monogramGlyph = contractMonogramGlyph;

/**
 * A wire payload with no currency predates the epic, and pre-epic money
 * is GOLD. Applied at the PRESENTATION boundary only (G3) — never used
 * to fill in a currency on something being sent.
 */
export function currencyOrGold(code: string | undefined | null): CurrencyCode {
  return isCurrencyCode(code) ? code : "GOLD";
}

/**
 * A wire amount in this console's ENTRY unit — what an operator types.
 *
 * The unit comes from the SM-443 presentation contract (`unitMinor`),
 * which is the ONE place any app decides what one unit of a currency is.
 * Since SM-460 that is the same $1 unit for all three (1 unit = wire
 * 10 000): SM-484 had left GOLD on the legacy cent entry unit, which is
 * why an operator could type "500" into a funding box and move $5. This
 * console and web-backoffice each held a copy of that table, both
 * disagreeing with the contract by 100x; the copies are gone, so there is
 * no longer a second table to keep in sync.
 *
 * Pairs with `parseUnitToMinor` below — the two are inverses, and they
 * live together so a change to one cannot silently miss the other. A
 * form whose display unit and parse unit disagree turns "500" into
 * "50,000" on a round trip.
 */
export function unitFromMinor(minor: number | null | undefined, code: CurrencyCode): string {
  if (minor === undefined || minor === null) return "";
  return String(minor / unitMinorFor(code));
}

/**
 * Inverse of `unitFromMinor`: an operator-typed amount → wire minor units.
 *
 * `parseSparkToMinor` already shifts a typed amount by the module's own display
 * unit (SPARK_UNIT_MINOR); the ratio below re-expresses that in THIS currency's
 * unit. It is 1 for all three currencies today, and it is written as a ratio
 * rather than dropped so that a future currency with a different unit cannot
 * silently inherit GOLD's.
 */
export function parseUnitToMinor(input: string, code: CurrencyCode): number {
  const base = Number(parseSparkToMinor(input)); // input × SPARK_UNIT_MINOR, comma-tolerant
  return base * (unitMinorFor(code) / SPARK_UNIT_MINOR);
}

/**
 * Wire minor units per displayed/entered unit for `code`, from the
 * contract's seed. Not exported: every caller should go through the
 * pair above or through <CurrencyAmount>, and re-exporting a raw divisor
 * is how both consoles grew their own table last time.
 */
function unitMinorFor(code: CurrencyCode): number {
  return resolveCurrencyPresentation(code).unitMinor;
}

/**
 * The currencies a broker money page can be scoped to, in the order the
 * wire lists pools (GOLD, RED, GREEN). Money pages always show ONE of
 * them — nothing in the portal sums across currencies (B9).
 */
export const BROKER_CURRENCIES: readonly CurrencyCode[] = ["GOLD", "RED", "GREEN"];

/** The shell's currency when the URL names none. */
export const DEFAULT_CURRENCY: CurrencyCode = "GOLD";

/**
 * The exchange's own broker (brokerv1.SystemBrokerID): users without a
 * broker sit under it. It is the ONLY node that trades in game mode
 * (SM-866) — every other broker trades CASH, so GOLD only.
 */
export const SYSTEM_BROKER_ID = "SYSTEM_BROKER";

/**
 * What a node trades when the server names nothing (a server that predates
 * the field, or an empty list): GOLD on every broker, and GOLD, RED and
 * GREEN on the SYSTEM_BROKER — the only node in game mode (SM-866/SM-869).
 */
export function defaultNodeCurrencies(brokerId: string | null | undefined): CurrencyCode[] {
  return brokerId === SYSTEM_BROKER_ID ? [...BROKER_CURRENCIES] : [DEFAULT_CURRENCY];
}

/**
 * The currencies the selected node trades, in BROKER_CURRENCIES order —
 * /path's `currencies`, which broker-service computes from the node's
 * trading modes (SM-851; the mapping stays in Go). Only the SYSTEM_BROKER
 * trades in game mode (SM-866): every broker node reads ["GOLD"], so RED
 * and GREEN are never offered there. When the server names nothing known
 * (an older server, an empty list) the node gets defaultNodeCurrencies:
 * GOLD, or all three on the SYSTEM_BROKER.
 */
export function nodeCurrencies(fromServer: readonly string[] | null | undefined, brokerId: string | null | undefined): CurrencyCode[] {
  const offered = BROKER_CURRENCIES.filter((c) => fromServer?.includes(c));
  return offered.length > 0 ? offered : defaultNodeCurrencies(brokerId);
}

/**
 * The shell's currency on a node: the URL's when the node trades it, else
 * the node's first (an old ?currency=RED link on a CASH-only node lands on
 * GOLD).
 */
export function currencyOnNode(requested: CurrencyCode, offered: readonly CurrencyCode[]): CurrencyCode {
  return offered.includes(requested) ? requested : (offered[0] ?? DEFAULT_CURRENCY);
}

/**
 * The one currency broker exposure exists in — brokerv1.ExposureCurrency.
 * Exposure is third-party credit, which exists in GOLD only (SM-768 B7),
 * and broker-service answers 400 to any other currency on the exposure
 * reads, so the Risk page reads this one whatever the shell selects
 * (SM-848).
 */
export const EXPOSURE_CURRENCY: CurrencyCode = "GOLD";
